<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          icon: 'i-woot-priority-empty',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          icon: 'i-woot-priority-urgent',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          icon: 'i-woot-priority-high',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          icon: 'i-woot-priority-medium',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          icon: 'i-woot-priority-low',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
        });
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
    isInbox204Layout() {
      return Number(this.currentChat?.inbox_id) === 204;
    },
    isPersonalConversation() {
      const labels = Array.isArray(this.currentChat?.labels)
        ? this.currentChat.labels
        : [];
      const attrs = this.currentChat?.additional_attributes || {};
      const inbox = this.$store.getters['inboxes/getInbox'](
        this.currentChat?.inbox_id
      );
      const inboxAttrs = inbox?.additional_attributes || {};

      return (
        labels.includes('pessoal') ||
        isAceleresIndividualOwn(inboxAttrs.connection_type) ||
        Number(attrs.personal_owner_chatwoot_user_id) > 0 ||
        Number(attrs.personal_origin_chatwoot_user_id) > 0 ||
        Number(attrs.owner_chatwoot_user_id) > 0
      );
    },
  },
  methods: {
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },
  },
};
</script>

<template>
  <div :class="{ 'conversation-action--inbox-204': isInbox204Layout }">
    <div class="conversation-action__section">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div class="conversation-action__section">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div class="conversation-action__section">
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <div
      v-if="!isPersonalConversation"
      class="conversation-action__section conversation-action__labels-section"
    >
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
      />
      <ConversationLabels :conversation-id="conversationId" />
    </div>
  </div>
</template>

<style scoped>
.conversation-action--inbox-204 {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0 0 1rem;
}

.conversation-action--inbox-204 .conversation-action__section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.conversation-action--inbox-204 :deep(.overflow-auto) {
  padding: 0;
}

.conversation-action--inbox-204 :deep(.relative.w-full.mb-2) {
  min-height: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
}

.conversation-action--inbox-204 :deep(.relative.w-full.mb-2 > button) {
  height: 2.75rem;
  min-height: 2.75rem;
  border: 1px solid #dfe3e8 !important;
  border-radius: 10px !important;
  background: #fff !important;
  box-shadow: none !important;
}

.conversation-action--inbox-204 :deep(.relative.w-full.mb-2 > div.absolute) {
  top: calc(100% + 0.5rem) !important;
}

.conversation-action--inbox-204 :deep(.dropdown-wrap > div.flex-auto) {
  margin-bottom: 0;
  max-height: none;
}

.conversation-action--inbox-204
  :deep(
    .relative.w-full.mb-2
      > div.absolute
      .dropdown-menu__item
      + .dropdown-menu__item
  ) {
  margin-top: 0.5rem;
}

.conversation-action--inbox-204
  :deep(.relative.w-full.mb-2 > div.absolute .dropdown-menu__item button) {
  height: 40px;
  min-height: 40px;
}

.conversation-action--inbox-204 :deep(.search-input) {
  height: 1.75rem;
  min-height: 1.75rem;
  border-radius: 50px;
}

.conversation-action--inbox-204 .conversation-action__labels-section {
  gap: 0.75rem;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  overflow: visible;
}

.conversation-action--inbox-204
  .conversation-action__labels-section
  :deep(.contact-conversation--list) {
  box-sizing: border-box;
  display: block;
  width: 100%;
  min-width: 0;
  padding: 1.5rem;
  border: 1px solid #dfe3e8;
  border-radius: 1.5rem;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  overflow: visible;
}

.conversation-action--inbox-204
  .conversation-action__labels-section
  :deep(.label-wrap) {
  display: block;
}

.conversation-action--inbox-204
  .conversation-action__labels-section
  :deep(.conversation-labels-controls) {
  display: flex;
  min-width: 0;
  align-items: stretch;
  gap: 1.5rem;
}

.conversation-action--inbox-204
  .conversation-action__labels-section
  :deep(.conversation-labels-active) {
  width: 100%;
  margin-top: 0.75rem;
}

.conversation-action--inbox-204
  .conversation-action__labels-section
  :deep(.sidebar-labels-wrap) {
  min-width: 0;
  margin: 0;
}

@media (max-width: 640px) {
  .conversation-action--inbox-204
    .conversation-action__labels-section
    :deep(.contact-conversation--list) {
    padding: 1rem;
    border-radius: 1rem;
  }

  .conversation-action--inbox-204
    .conversation-action__labels-section
    :deep(.conversation-labels-controls) {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .conversation-action--inbox-204
    .conversation-action__labels-section
    :deep(.conversation-labels-add-button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
